import React, { useState, Fragment } from "react";
import * as PDFJS from "pdfjs-dist/legacy/build/pdf";
import styled, { keyframes } from "styled-components";
import theme from "../../../assets/theme";
import SearchBar from "../../common/CustomTable/common/SearchBar";
import { Modal } from "@material-ui/core";
import workerSrc from "pdfjs-dist/legacy/build/pdf.worker.entry";
import useLoader from "../../../hooks/useLoader";
import { getErrorMessage } from "../../../helpers/functions";
import { useSnackbar } from "notistack";

PDFJS.GlobalWorkerOptions.isEvalSupported = false;
PDFJS.GlobalWorkerOptions.workerSrc = workerSrc;

const PdfViewer = ({ urls = [], bundleCode }) => {
  const [modal, setModal] = useState(false);
  const [inputText, setInputText] = useState("");
  const [results, setResults] = useState([]);
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  const handleSearch = async () => {
    if (!urls.length) {
      enqueueSnackbar("PDF URLs missing", { variant: "error" });
      return;
    }
    if (!inputText.trim()) return;

    let found = [];

    try {
      setLoader({ state: true, message: "Searching PDFs..." });

      for (let fileIndex = 0; fileIndex < urls.length; fileIndex++) {
        const pdfUrl = urls[fileIndex];
        const response = await fetch(pdfUrl);
        if (!response.ok) continue;

        const buffer = await response.arrayBuffer();
        const pdf = await PDFJS.getDocument({ data: buffer }).promise;

        for (let pageIndex = 1; pageIndex <= pdf.numPages; pageIndex++) {
          const pageObj = await pdf.getPage(pageIndex);
          const textContent = await pageObj.getTextContent();
          const text = textContent.items
            .map((item) => item.str)
            .join(" ")
            .toLowerCase();

          if (text.includes(inputText.toLowerCase())) {
            found.push({ url: pdfUrl, page: pageIndex });
          }
        }
      }

      if (found.length) {
        setResults(found);
        setModal(true);
      } else {
        enqueueSnackbar("Search keyword not found", { variant: "info" });
      }
    } catch (err) {
      const message = getErrorMessage(err);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <Fragment>
      <SearchContainer>
        <SearchBar
          placeHolderText="Search keyword"
          backgroundColor="#ffffff"
          {...{ inputText, setInputText }}
          onKeyDown={handleKeyDown}
        />
        {inputText?.length ? (
          <SearchBtn onClick={handleSearch} aria-label="Search">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <span>Search</span>
          </SearchBtn>
        ) : null}
      </SearchContainer>

      <Modal open={modal} onClose={() => setModal(false)}>
        <Overlay>
          <ResultsCard>
            <TopAccent />
            <ResultsHeader>
              <ResultsIconCircle>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </ResultsIconCircle>
              <ResultsHeaderText>
                <ResultsTitle>Search Results</ResultsTitle>
                <ResultsSubtitle>
                  {results?.length
                    ? `Found on ${results.length} page${results.length > 1 ? "s" : ""}`
                    : "Searching..."}
                </ResultsSubtitle>
              </ResultsHeaderText>
              <CloseBtn onClick={() => setModal(false)}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </ResultsHeader>

            <ModalDivider />

            <ResultsKeyword>
              <KeywordLabel>Keyword</KeywordLabel>
              <KeywordChip>"{inputText}"</KeywordChip>
            </ResultsKeyword>

            <ResultsList>
              {results.map((r, i) => (
                <ResultItem
                  key={i}
                  onClick={() => window.open(`${r.url}#page=${r.page}`)}
                >
                  <ResultLeft>
                    <PageBadge>pg {r.page}</PageBadge>
                    <ResultLink>
                      {bundleCode} — Page {r.page}
                    </ResultLink>
                  </ResultLeft>
                  <OpenIcon>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </OpenIcon>
                </ResultItem>
              ))}
            </ResultsList>
          </ResultsCard>
        </Overlay>
      </Modal>
    </Fragment>
  );
};

export default PdfViewer;

/* ── Animations ── */
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -46%) scale(0.96); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

/* ── Styles ── */
const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 15px;

  @media ${theme?.breakpoints?.sm_up} {
    margin: 0;
  }
`;

const SearchBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  height: 36px;
  background: transparent;
  border: 1.5px solid #00838c;
  border-radius: 10px;
  color: #00838c;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    background: #00838c;
    color: #fff;
    box-shadow: 0 4px 12px rgba(0, 131, 140, 0.25);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 400px) {
    span {
      display: none;
    }
    padding: 0 10px;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
`;

const ResultsCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 24px);
  max-width: 400px;
  background: #ffffff;
  border-radius: 24px;
  box-shadow:
    0 32px 80px rgba(0, 0, 0, 0.2),
    0 8px 24px rgba(0, 131, 140, 0.12);
  animation: ${fadeSlideIn} 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;

  @media (max-width: 480px) {
    border-radius: 20px 20px 0 0;
    top: auto;
    bottom: 0;
    left: 0;
    transform: none;
    width: 100%;
    max-width: 100%;
  }
`;

const TopAccent = styled.div`
  height: 4px;
  background: linear-gradient(90deg, #00838c 0%, #00b4c0 50%, #00838c 100%);
`;

const ResultsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 20px 16px;
`;

const ResultsIconCircle = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: linear-gradient(135deg, #e6f7f8 0%, #b2e8eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #00838c;
`;

const ResultsHeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const ResultsTitle = styled.h2`
  margin: 0;
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 17px;
  color: #293461;
`;

const ResultsSubtitle = styled.p`
  margin: 3px 0 0;
  font-size: 12px;
  color: #64748b;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const CloseBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s ease;
  flex-shrink: 0;
  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #ef4444;
    transform: rotate(90deg);
  }
`;

const ModalDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    #e2e8f0 30%,
    #e2e8f0 70%,
    transparent
  );
  margin: 0 20px;
`;

const ResultsKeyword = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px 10px;
`;

const KeywordLabel = styled.span`
  font-size: 11px;
  color: #94a3b8;
  font-family: ${theme.fonts.primaryFontSemiBold};
  text-transform: uppercase;
  letter-spacing: 0.6px;
  white-space: nowrap;
`;

const KeywordChip = styled.span`
  font-size: 13px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #00838c;
  background: linear-gradient(135deg, #f0fdfb 0%, #e6f7f8 100%);
  border: 1.5px solid #99f6e4;
  padding: 4px 12px;
  border-radius: 100px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ResultsList = styled.div`
  padding: 4px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 280px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 2px;
  }
`;

const ResultItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fafafa;

  &:hover {
    border-color: #00838c;
    background: linear-gradient(135deg, #f0fdfb 0%, #e6f7f8 100%);
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(0, 131, 140, 0.12);
  }
`;

const ResultLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const PageBadge = styled.span`
  font-size: 11px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #fff;
  background: #00838c;
  padding: 3px 9px;
  border-radius: 6px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ResultLink = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OpenIcon = styled.div`
  color: #94a3b8;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: color 0.2s;

  ${ResultItem}:hover & {
    color: #00838c;
  }
`;

export const Label = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  line-height: 1.9;
  margin-top: 12px;
  color: #64748b;
  display: inline-block;
`;

export const HyperLink = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: #00838c;
  text-decoration: underline;
  cursor: pointer;
  display: inline-block;
  transition: color 0.2s;

  &:hover {
    color: #006b73;
  }
`;
